<template>
    <div class="tab-content">
        <div v-if="isLoadingSetting">
            <loader :margin="0" />
        </div>

        <div v-else>
            <div class="statistics-wrapper">
                <snapshot-chart
                    id="vital-sign-weight"
                    :chart-data="weightChartData"
                    :xaxis-labels="weightChartXAxisData"
                    title="Weight"
                    titleIcon="fas fa-weight"
                    :value="weightValue"
                    :description="weightValueDesc"
                    :unit="weightValueUnit"
                    @chart-clicked="handleChartClick"
                />
                <snapshot-chart
                    id="vital-sign-temperature"
                    :chart-data="temperatureChartData"
                    :xaxis-labels="temperatureChartXAxisData"
                    title="Temperature"
                    titleIcon="fas fa-thermometer-half"
                    :value="temperatureValue"
                    :description="temperatureValueDesc"
                    :unit="temperatureValueUnit"
                    @chart-clicked="handleChartClick"
                />
                <snapshot-chart
                    id="vital-sign-heartrate"
                    :chart-data="heartRateChartData"
                    :xaxis-labels="heartRateChartXAxisData"
                    title="Heart Rate"
                    titleIcon="fas fa-heartbeat"
                    :value="heartRateValue"
                    :description="heartRateValueDesc"
                    :unit="heartRateValueUnit"
                    @chart-clicked="handleChartClick"
                />
                <snapshot-chart
                    id="vital-sign-respiration"
                    :chart-data="respirationChartData"
                    :xaxis-labels="respirationChartXAxisData"
                    title="Respiration"
                    titleIcon='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path d="M636.1 390.2C614.4 308.9 580.1 231 534.1 159.1 512 124.6 498 96 454.1 96 415.4 96 384 125.4 384 161.7v60.1l-32.9-21.9a16 16 0 0 1 -7.1-13.3V16c0-8.8-7.2-16-16-16h-16c-8.8 0-16 7.2-16 16v170.6c0 5.4-2.7 10.3-7.1 13.3L256 221.8v-60.1C256 125.4 224.6 96 186 96c-44 0-57.9 28.6-80.1 63.1C59.9 231 25.6 308.9 3.9 390.2 1.3 399.8 0 409.8 0 419.8c0 61.2 62.5 105.4 125.2 88.6l59.5-16c42.2-11.3 71.3-47.5 71.3-88.6v-87.5l-85.8 57.2a8 8 0 0 1 -11.1-2.2l-8.9-13.3a8 8 0 0 1 2.2-11.1L320 235.2l167.6 111.7a8 8 0 0 1 2.2 11.1l-8.9 13.3a8 8 0 0 1 -11.1 2.2L384 316.3v87.5c0 41.2 29.1 77.3 71.3 88.6l59.5 16C577.5 525.2 640 481 640 419.8c0-10-1.3-19.9-3.9-29.6z"/></svg>'
                    :value="respirationValue"
                    :description="respirationValueDesc"
                    :unit="respirationValueUnit"
                    @chart-clicked="handleChartClick"
                />
            </div>

            <div class="lower-details">
                <div>
                    <medical-notes
                        :patientId="patientId"
                        :isSuperUser="isSuperUser"
                        :permissions="userPermissions"
                        :loadingPermissions="isPermissionLoading"
                        :currentBusinessId="currentBusinessId"
                        :skipFetchData="skipFetchData"
                    />
                    <open-diagnoses
                        :patientId="patientId"
                        :isSuperUser="isSuperUser"
                        :permissions="userPermissions"
                        :loadingPermissions="isPermissionLoading"
                        :currentBusinessId="currentBusinessId"
                        :skipFetchData="skipFetchData"
                    />
                </div>
                <div>
                    <current-plan
                        :patientId="patientId"
                        :isSuperUser="isSuperUser"
                        :permissions="userPermissions"
                        :loadingPermissions="isPermissionLoading"
                        :currentBusinessId="currentBusinessId"
                        :skipFetchData="skipFetchData"
                        :patientDetails="patientDetails"
                    />

                    <communications
                        :patientId="patientId"
                        :patientDetails="patientDetails"
                        :isSuperUser="isSuperUser"
                        :permissions="userPermissions"
                        :loadingPermissions="isPermissionLoading"
                        :currentBusinessId="currentBusinessId"
                        :skipFetchData="skipFetchData"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
$ = window.$;
import snapshotChart from "./snapshot-chart.vue";
import openDiagnoses from "./open-diagnoses.vue";
import currentPlan from "./current-plan.vue";
import communications from "./communications.vue";
import medicalNotes from "./medical-notes.vue";
import axios from "axios";
import Loader from "@/components/elements/loader";

export default {
    components: {
        snapshotChart,
        openDiagnoses,
        currentPlan,
        communications,
        medicalNotes,
        Loader
    },
    props: {
        patientDetails: {
            type: Object
        }
    },
    data: function () {
        return {
            isLoadingSetting: true,
            isWeightLoading: true,
            isTemperatureLoading: true,
            isHeartRateLoading: true,
            isRespirationLoading: true,
            weightValue: "",
            weightValueUnit: "",
            weightChartData: [],
            weightChartXAxisData: [],
            weightRecords: [],
            temperatureValue: "",
            temperatureValueUnit: "",
            temperatureChartData: [],
            temperatureChartXAxisData: [],
            temperatureRecords: [],
            heartRateValue: "",
            heartRateValueUnit: "",
            heartRateChartData: [],
            heartRateChartXAxisData: [],
            heartRateRecords: [],
            respirationValue: "",
            respirationValueUnit: "",
            respirationChartData: [],
            respirationChartXAxisData: [],
            respirationRecords: [],
            isPermissionLoading: true,
            isSuperUser: false,
            userPermissions: {},
            currentBusinessId: null
        };
    },
    mounted: async function () {
        const settings = await this._getSetting();
        this.isLoadingSetting = false;
        this.currentBusinessId = settings.business_id;

        if (this.patientId) {
            this.getVitalData(this.patientId); // TODO: how to handle jquery plot?
            this.getPermission();
        } else {
            this.isWeightLoading = false;
            this.isTemperatureLoading = false;
            this.isHeartRateLoading = false;
            this.isRespirationLoading = false;
        }
    },
    computed: {
        patientId() {
            return this.patientDetails ? this.patientDetails.id : null;
        },
        skipFetchData: function () {
            return this.patientId ? false : true;
        },
        weightValueDesc: function () {
            return this.isWeightLoading
                ? "..."
                : this.getStatsBoxSecondLabel(this.weightRecords);
        },
        temperatureValueDesc: function () {
            return this.isTemperatureLoading
                ? "..."
                : this.getStatsBoxSecondLabel(this.temperatureRecords);
        },
        heartRateValueDesc: function () {
            return this.isHeartRateLoading
                ? "..."
                : this.getStatsBoxSecondLabel(this.heartRateRecords);
        },
        respirationValueDesc: function () {
            return this.isRespirationLoading
                ? "..."
                : this.getStatsBoxSecondLabel(this.respirationRecords);
        }
    },
    methods: {
        async getPermission() {
            let superUser = false;

            if (superUser) {
                this.isSuperUser = true;
                this.isPermissionLoading = false;
            } else {
                this.isSuperUser = false;
                this.userPermissions = await this.GetUserPermissions();
                this.isPermissionLoading = false;
            }
        },
        async GetUserPermissions() {
            let res = await axios.get(
                process.env.ACTOR_SERVICE + "staff/current/permissions"
            );

            if (res.data.response.message.code === 200) {
                return res.data.response.resource;
            }

            return {};
        },
        async generateChartData(sources = []) {
            if (sources.length === 0) {
                return {
                    data: [],
                    xAxisLabels: []
                };
            }

            var data = [];
            var xAxisLabels = [];
            const currentUOM = sources[sources.length - 1].master_uom.uom; // Ensure all chart points use the current UOM

            for (var n = 0; n < sources.length; n++) {
                var record = sources[n];
                const {
                    value,
                    master_uom: { uom }
                } = record;

                let yAxis = Number(value);

                // Compare to current UOM
                if (uom !== currentUOM) {
                    yAxis = this.convertUOM(value, uom, currentUOM);
                }

                data.push([n, yAxis]);

                // Add date to xAxisLabels
                if (record.date) {
                    const dateString = this._carbonDateDisplay(
                        record.date,
                        "MMM dd, yy"
                    );
                    if (dateString !== "--") {
                        // Wrap the label in an inner div to counteract the margin from chart.vue
                        const centeredLabelHtml = `<div style="display: inline-block; margin-left: -30px; text-align: center;">${dateString}</div>`;
                        xAxisLabels.push([n, centeredLabelHtml]);
                    }
                }
            }

            return {
                data: data,
                xAxisLabels: xAxisLabels
            };
        },
        async applyVital(
            records = [],
            recordKey,
            mainKey,
            unitKey,
            chartKey,
            xAxisKey
        ) {
            // Reverse the records to be newest-to-oldest
            let reversedRecords = records.reverse();

            if (reversedRecords.length > 4) {
                let length = reversedRecords.length;
                reversedRecords = reversedRecords.slice(length - 4, length);
            }

            this[recordKey] = reversedRecords;
            var lastRecord = reversedRecords[reversedRecords.length - 1];

            if (lastRecord) {
                var { value, uom_unit } = lastRecord;

                this[mainKey] = value + " " + uom_unit;
                this[unitKey] = uom_unit;
            } else {
                this[mainKey] = "N/A";
                this[unitKey] = "";
            }

            const chartData = await this.generateChartData(this[recordKey]);

            this[chartKey] = chartData.data;
            this[xAxisKey] = chartData.xAxisLabels;
        },
        async getVitalData(patientId) {
            let uri = `${process.env.PATIENT_SERVICE}${patientId}/vitals?vital_id[]=1&vital_id[]=2&vital_id[]=3&vital_id[]=4`;
            let res = await axios.get(uri);

            this.isWeightLoading = false;
            this.isTemperatureLoading = false;
            this.isHeartRateLoading = false;
            this.isRespirationLoading = false;

            if (res.data.response.message.code === 200) {
                var recordWeight = res.data.response.resources[4];
                var recordTemperature = res.data.response.resources[3];
                var recordHeartRate = res.data.response.resources[1];
                var recordRespiration = res.data.response.resources[2];

                this.applyVital(
                    recordWeight,
                    "weightRecords",
                    "weightValue",
                    "weightValueUnit",
                    "weightChartData",
                    "weightChartXAxisData"
                );

                this.applyVital(
                    recordTemperature,
                    "temperatureRecords",
                    "temperatureValue",
                    "temperatureValueUnit",
                    "temperatureChartData",
                    "temperatureChartXAxisData"
                );

                this.applyVital(
                    recordHeartRate,
                    "heartRateRecords",
                    "heartRateValue",
                    "heartRateValueUnit",
                    "heartRateChartData",
                    "heartRateChartXAxisData"
                );

                this.applyVital(
                    recordRespiration,
                    "respirationRecords",
                    "respirationValue",
                    "respirationValueUnit",
                    "respirationChartData",
                    "respirationChartXAxisData"
                );
            }
        },
        getStatsBoxSecondLabel(records = []) {
            if (records.length === 0) {
                return "N/A";
            }

            if (records.length === 1) {
                return "- vs last record";
            }

            const [currentRecord, previousRecord] = records
                .reverse()
                .slice(0, 2)
                .map(record => ({
                    value: Number(record.value),
                    uom: record.master_uom.uom
                }));

            // Compare to current UOM
            if (previousRecord.uom !== currentRecord.uom) {
                previousRecord.value = this.convertUOM(
                    previousRecord.value,
                    previousRecord.uom,
                    currentRecord.uom
                );
            }

            var diff = currentRecord.value - previousRecord.value;
            var diffPercentage = (diff / previousRecord.value) * 100;

            return `${diffPercentage.toFixed(0)}% vs last record`;
        },
        handleChartClick(chartId) {
            let vitalType = null;
            switch (chartId) {
                case "vital-sign-weight":
                    vitalType = "WEIGHT"; // Corresponds to CVITAL.TYPE.WEIGHT (actual value is 4)
                    break;
                case "vital-sign-temperature":
                    vitalType = "TEMPERATURE"; // Corresponds to CVITAL.TYPE.TEMPERATURE (actual value is 3)
                    break;
                case "vital-sign-heartrate":
                    vitalType = "HEARTRATE"; // Corresponds to CVITAL.TYPE.HEARTRATE (actual value is 1)
                    break;
                case "vital-sign-respiration":
                    vitalType = "RESPIRATION"; // Corresponds to CVITAL.TYPE.RESPIRATION (actual value is 2)
                    break;
            }
            if (vitalType) {
                this.$emit("view-vitals", vitalType);
            }
        },
        convertUOM(value, fromUOM, toUOM) {
            value = Number(value);

            const weightUnits = {
                G: 1,
                Kg: 1000,
                Lbs: 453.592,
                Oz: 28.3495
            };

            const isWeight = weightUnits[fromUOM] && weightUnits[toUOM];
            const isTemperature = fromUOM === "C" || fromUOM === "F";

            // Weight conversion
            if (isWeight) {
                const grams = value * weightUnits[fromUOM];
                const converted = grams / weightUnits[toUOM];
                return Number(converted.toFixed(2));
            }

            // Temperature conversion
            if (isTemperature) {
                const converted =
                    fromUOM === "C"
                        ? (value * 9) / 5 + 32
                        : ((value - 32) * 5) / 9;
                return Number(converted.toFixed(2));
            }
        }
    }
};
</script>

<style scoped>
.statistics-wrapper {
    display: grid;
    grid-template-columns: 1fr;
    column-gap: 15px;
    margin-bottom: 50px;
    row-gap: 15px;
    grid-auto-rows: 1fr;
}

.lower-details {
    display: grid;
    grid-template-columns: 1fr;
}
.lower-details > div {
    padding: 0;
}

@media (min-width: 768px) {
    .statistics-wrapper {
        grid-template-columns: 1fr 1fr;
        margin-top: 10px;
        margin-bottom: 30px;
    }
}

@media (min-width: 992px) {
    .lower-details {
        grid-template-columns: 1fr 1fr;
        column-gap: 35px;
        margin: 10px 0;
    }
    .lower-details > div {
        padding: 10px;
    }
}

@media (min-width: 1700px) {
    .statistics-wrapper {
        grid-template-columns: 1fr 1fr 1fr 1fr;
        margin-top: 10px;
        margin-bottom: 30px;
    }
}
</style>
